import { useIsPlatformAdmin, useSession } from "@/contexts/Auth";
import { Scope } from "@gram/client/models/components/rolegrant.js";
import { useGrants } from "@gram/client/react-query/grants.js";
import { useCallback, useEffect, useMemo, useState } from "react";

// Open SDK enums accept future values, but only understood scopes may authorize.
const knownScopes: ReadonlySet<string> = new Set(Object.values(Scope));

/**
 * Derive the resource kind from a scope's family prefix.
 * Mirrors the server-side ResourceKindForScope in authz/selector.go.
 * mcp:auth is not checkable client-side: the server binds the connection owner.
 */
export function resourceKindForScope(scope: string): string {
  if (scope === "mcp:auth" || scope === "mcp:blocked_auth")
    return "mcp_auth_client";
  if (
    scope.startsWith("project:") ||
    scope.startsWith("plugin:") ||
    scope.startsWith("log:")
  )
    return "project";
  if (scope.startsWith("remote-mcp:") || scope.startsWith("mcp:")) return "mcp";
  if (scope.startsWith("org:")) return "org";
  if (scope.startsWith("environment:")) return "environment";
  if (scope.startsWith("skill:")) return "skill";
  if (scope.startsWith("assistant:")) return "assistant";
  if (scope.startsWith("risk_policy:")) return "risk_policy";
  if (scope.startsWith("chat:")) return "chat";
  if (scope.startsWith("agent:")) return "agent";
  if (scope.startsWith("workload:")) return "workload";
  return "*";
}

/**
 * Check if a grant selector matches a check selector.
 * Mirrors the server-side Selector.Matches in authz/selector.go.
 *
 * For each key in the grant selector, if the check also has that key the
 * values must match (or the grant value must be "*"). Keys present in the
 * grant but absent from the check are skipped — the check isn't constraining
 * that dimension.
 */
export function selectorMatches(
  grantSelector: Record<string, string>,
  checkSelector: Record<string, string>,
): boolean {
  for (const [key, grantVal] of Object.entries(grantSelector)) {
    const checkVal = checkSelector[key];
    if (checkVal === undefined) continue;
    if (grantVal !== "*" && grantVal !== checkVal) return false;
  }
  return true;
}

/** Mirrors Selector.StrictMatches for exclusion grants. */
export function selectorMatchesStrict(
  grantSelector: Record<string, string>,
  checkSelector: Record<string, string>,
): boolean {
  for (const [key, grantVal] of Object.entries(grantSelector)) {
    const checkVal = checkSelector[key];
    if (checkVal === undefined) return false;
    if (grantVal !== "*" && grantVal !== checkVal) return false;
  }
  return true;
}

/** Direct exclusion scope plus its one-step server scope expansions. */
const exclusionScopesByScope: Partial<Record<Scope, readonly string[]>> = {
  "org:read": ["org:blocked_read"],
  "org:admin": ["org:blocked_admin", "org:blocked_read"],
  "project:read": ["project:blocked_read"],
  "project:write": ["project:blocked_write", "project:blocked_read"],
  // The mcp:blocked_* scopes are independent of one another, unlike the
  // other families: connecting to a server and administering it are
  // different jobs, so a block on one takes nothing from the others. Mirrors
  // scopeExpansions in server/internal/authz/scopes.go.
  "mcp:read": ["mcp:blocked_read"],
  "mcp:write": ["mcp:blocked_write"],
  "mcp:connect": ["mcp:blocked_connect"],
  "mcp:auth": ["mcp:blocked_auth"],
  "environment:read": ["environment:blocked_read"],
  "environment:write": [
    "environment:blocked_write",
    "environment:blocked_read",
  ],
  "plugin:write": ["plugin:blocked_write"],
  "skill:read": ["skill:blocked_read"],
  "skill:write": ["skill:blocked_write", "skill:blocked_read"],
  "assistant:read": ["assistant:blocked_read"],
  "assistant:write": ["assistant:blocked_write", "assistant:blocked_read"],
  "risk_policy:evaluate": ["risk_policy:bypass"],
};

export function exclusionScopesForScope(scope: Scope): readonly string[] {
  return exclusionScopesByScope[scope] ?? [];
}

interface EffectiveGrant {
  scope?: string;
  selectors?: Array<Record<string, string>>;
  /**
   * Selectors granted to the caller by name. For allow scopes these name a
   * concrete resource and outrank blocks inherited from a role or everyone;
   * for blocked scopes they are the caller's own blocks, which always apply.
   */
  directSelectors?: Array<Record<string, string>>;
  subScopes?: string[];
  effect?: string;
}

function grantSelectorsMatch(
  grant: EffectiveGrant,
  check: Record<string, string>,
  strict: boolean,
): boolean {
  if (!grant.selectors) return true;
  const matches = strict ? selectorMatchesStrict : selectorMatches;
  return grant.selectors.some((selector) => matches(selector, check));
}

function directSelectorsMatch(
  grant: EffectiveGrant,
  check: Record<string, string>,
  strict: boolean,
): boolean {
  const matches = strict ? selectorMatchesStrict : selectorMatches;
  return (grant.directSelectors ?? []).some((selector) =>
    matches(selector, check),
  );
}

/**
 * Blocklist exclusions (`*:blocked_*`) yield to a direct grant naming the
 * resource. Other exclusions such as risk_policy:bypass never do. Mirrors
 * ExclusionYieldsToDirectGrants in server/internal/authz/precedence.go.
 */
function exclusionYieldsToDirectGrants(exclusionScope: string): boolean {
  return exclusionScope.includes(":blocked_");
}

/**
 * Pure equivalent of hasScope for loaded effective grants. Multiple resource IDs
 * accept any allow, but an exclusion on ANY alternative wins (RequireAnyUnblocked).
 * A block inherited from a role or everyone does not win on an alternative
 * that a direct grant naming the resource satisfies; the caller's own block
 * always does.
 */
export function hasScopeInGrants(
  grants: EffectiveGrant[],
  scope: Scope,
  resourceId?: string | readonly string[],
  projectId?: string,
): boolean {
  if (!knownScopes.has(scope)) return false;
  // Only the server can bind the connection owner of an auth-client check.
  if (resourceKindForScope(scope) === "mcp_auth_client") return false;

  const resourceIds =
    typeof resourceId === "string" || resourceId === undefined
      ? [resourceId]
      : resourceId;
  const allowChecks = resourceIds.map((id) => {
    const allowCheck: Record<string, string> = {
      resourceKind: resourceKindForScope(scope),
    };
    if (id) allowCheck.resourceId = id;
    if (projectId) allowCheck.projectId = projectId;
    return allowCheck;
  });
  // Unscoped allows are existential, but strict exclusions must distinguish
  // unrestricted wildcards from exclusions for one concrete resource.
  const exclusionChecks = allowChecks.map((check) =>
    check.resourceId ? check : { ...check, resourceId: "*" },
  );

  const exclusionScopes = exclusionScopesForScope(scope);
  let hasAllow = false;
  // Per alternative: whether a block that cannot be outranked matched, whether
  // a block inherited from a role or everyone matched, and whether a direct
  // grant naming the resource satisfies it.
  const absoluteBlock = allowChecks.map(() => false);
  const inheritedBlock = allowChecks.map(() => false);
  const directAllow = allowChecks.map(() => false);

  for (const grant of grants) {
    // An unknown parent must not authorize through its known sub-scopes either.
    if (grant.scope === undefined || !knownScopes.has(grant.scope)) continue;
    const effect = grant.effect || "allow";

    const isLegacyDeny = effect === "deny" && grant.scope === scope;
    const isExclusion =
      effect === "allow" &&
      grant.scope !== undefined &&
      exclusionScopes.includes(grant.scope);
    if (isLegacyDeny || isExclusion) {
      const yields =
        isExclusion && exclusionYieldsToDirectGrants(grant.scope ?? "");
      exclusionChecks.forEach((check, i) => {
        if (!grantSelectorsMatch(grant, check, true)) return;
        if (yields && !directSelectorsMatch(grant, check, true)) {
          inheritedBlock[i] = true;
        } else {
          absoluteBlock[i] = true;
        }
      });
    }

    const scopeMatches =
      grant.scope === scope || grant.subScopes?.includes(scope);
    if (effect === "allow" && scopeMatches) {
      allowChecks.forEach((check, i) => {
        if (grantSelectorsMatch(grant, check, false)) hasAllow = true;
        if (directSelectorsMatch(grant, check, false)) directAllow[i] = true;
      });
    }
  }

  const blocked = allowChecks.some(
    (_, i) => absoluteBlock[i] || (inheritedBlock[i] && !directAllow[i]),
  );
  return hasAllow && !blocked;
}

/** Any allow is sufficient, but an exclusion on any alternative wins. */
export function hasAnyUnblockedScopeInGrants(
  grants: EffectiveGrant[],
  checks: readonly { scope: Scope; resourceId: string; projectId?: string }[],
): boolean {
  return (
    checks.every(({ scope, resourceId, projectId }) =>
      // A synthetic allow probes only exclusions; real allows are checked below.
      hasScopeInGrants([...grants, { scope }], scope, resourceId, projectId),
    ) &&
    checks.some(({ scope, resourceId, projectId }) =>
      hasScopeInGrants(grants, scope, resourceId, projectId),
    )
  );
}

export function hasScopeInProject(
  grants: EffectiveGrant[],
  scope: Scope,
  projectId: string,
): boolean {
  return hasScopeInGrants(grants, scope, "*", projectId);
}

/**
 * Core RBAC hook. Fetches the current user's effective grants and provides
 * helpers to check whether the user holds a particular scope.
 */
function useRBACImpl() {
  const isAdmin = useIsPlatformAdmin();
  const session = useSession();

  // Re-render when the toolbar changes scopes in localStorage.
  const [, setOverrideVersion] = useState(0);
  useEffect(() => {
    if (!import.meta.env.DEV && !isAdmin) return;
    const handler = () => setOverrideVersion((v) => v + 1);
    window.addEventListener("rbac-override-change", handler);
    return () => window.removeEventListener("rbac-override-change", handler);
  }, [isAdmin]);

  // Always fetch grants so we can detect a broken org membership (404/403) and
  // show a recovery prompt via MembershipSyncGuard. throwOnError is disabled
  // so the error doesn't crash the app; it's surfaced via `error` instead.
  const { data, isLoading, error } = useGrants(
    { gramSession: session.session },
    undefined,
    {
      staleTime: 30_000,
      throwOnError: false,
    },
  );

  // The toolbar event re-renders this hook; query invalidation refreshes the grants.
  const grants = useMemo(() => {
    return data?.grants;
  }, [data?.grants]);

  /**
   * Check if the user has a given scope, optionally scoped to a resource ID
   * and the project that resource belongs to. Passing `projectId` lets
   * project-wide grants (`{ resourceId: "*", projectId }`) cover the resource
   * and keeps grants for other projects from matching.
   *
   * Uses exclusion-wins semantics: matching internal exclusion grants (and
   * legacy deny-effect grants) override matching allow grants.
   *
   * - If grants haven't loaded yet, returns false (safe default).
   * - A grant with `selectors: undefined` (null from the API) means unrestricted.
   * - A grant with `selectors: [...]` means the scope is constrained by selectors.
   */
  const hasScope = useCallback(
    (scope: Scope, resourceId?: string, projectId?: string): boolean => {
      if (!grants) return false;

      return hasScopeInGrants(grants, scope, resourceId, projectId);
    },
    [grants],
  );

  /**
   * Check multiple scopes at once. Returns true if the user has ALL of them.
   */
  const hasAllScopes = useCallback(
    (scopes: Scope[], resourceId?: string, projectId?: string): boolean => {
      return scopes.every((scope) => hasScope(scope, resourceId, projectId));
    },
    [hasScope],
  );

  /**
   * Check multiple scopes at once. Returns true if the user has ANY of them.
   */
  const hasAnyScope = useCallback(
    (scopes: Scope[], resourceId?: string, projectId?: string): boolean => {
      return scopes.some((scope) => hasScope(scope, resourceId, projectId));
    },
    [hasScope],
  );

  const hasAnyScopeInProject = useCallback(
    (scopes: Scope[], projectId: string): boolean => {
      return scopes.some((scope) =>
        hasScopeInProject(grants ?? [], scope, projectId),
      );
    },
    [grants],
  );

  return useMemo(
    () => ({
      hasScope,
      hasAllScopes,
      hasAnyScope,
      hasAnyScopeInProject,
      isLoading,
      grants: grants ?? [],
      /** Non-null when the grants query failed (e.g. missing org membership). */
      error: error ?? null,
    }),
    [
      hasScope,
      hasAllScopes,
      hasAnyScope,
      hasAnyScopeInProject,
      isLoading,
      grants,
      error,
    ],
  );
}

export function useRBAC(): ReturnType<typeof useRBACImpl> {
  return useRBACImpl();
}
